import { motion } from 'framer-motion';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import { HappyUsers } from '@/components/landing/HappyUsers';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import HailuoSvg from '~/svg/Hailuo.svg';
import MoonshotSvg from '~/svg/moonshot.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import PerplexitySvg from '~/svg/perplexity.svg';
import QwenSvg from '~/svg/qwen.svg';
import XaiSvg from '~/svg/xai.svg';

const PROVIDERS: Array<{
  name: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}> = [
  { name: 'GPT-5.6', Icon: OpenAISvg },
  { name: 'Claude Opus 5', Icon: AnthropicSvg },
  { name: 'Gemini 4', Icon: GoogleSvg },
  { name: 'DeepSeek V4', Icon: DeepseekSvg },
  { name: 'Grok 4.5', Icon: XaiSvg },
  { name: 'Kimi K3', Icon: MoonshotSvg },
  { name: 'Qwen 3.7', Icon: QwenSvg },
  { name: 'MiniMax M3', Icon: HailuoSvg },
  { name: 'Perplexity Sonar', Icon: PerplexitySvg },
];

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});

const MarkyHero = () => {
  const { t } = useLocale('marky');

  return (
    <section className='relative'>
      <div className='relative mx-auto max-w-7xl px-4 pt-20 pb-16 text-center sm:px-6 sm:pt-28 lg:px-8'>
        <motion.div {...fadeUp(0)}>
          <div className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-600 dark:border-white/15 dark:bg-white/5 dark:text-slate-300 sm:text-sm'>
            <span className='relative flex h-2 w-2'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:hidden' />
              <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-400' />
            </span>
            {t('Marky — your AI agent')}
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.1)}>
          <h1 className='mx-auto mt-8 max-w-4xl text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-6xl lg:text-7xl'>
            {t('Ask for anything.')}
            <span className='mt-1 block bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text pb-2 text-transparent dark:from-indigo-300 dark:via-violet-300 dark:to-fuchsia-300'>
              {t('Watch it get done.')}
            </span>
          </h1>
        </motion.div>

        <motion.div {...fadeUp(0.2)}>
          <p className='mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300 md:text-xl'>
            {t(
              'Marky chats with every top model — then actually does the work: it builds and ships websites, browses the web, runs code, and creates slides, images, video and music.',
            )}
          </p>
        </motion.div>

        <motion.div
          {...fadeUp(0.3)}
          className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'
        >
          <Link
            href='/login?preset=chat'
            className='group inline-flex h-[56px] min-w-[220px] items-center justify-center gap-2 rounded-full bg-indigo-600 px-8 text-lg font-bold text-white shadow-lg shadow-indigo-600/25 transition-all hover:bg-indigo-500 dark:bg-indigo-500 dark:hover:bg-indigo-400'
          >
            {t('Chat with Marky free')}
            <span
              aria-hidden='true'
              className='transition-transform group-hover:translate-x-1'
            >
              →
            </span>
          </Link>
          <a
            href='#capabilities'
            className='inline-flex h-[56px] min-w-[180px] items-center justify-center rounded-full border border-slate-300 px-8 text-lg font-semibold text-slate-700 transition-all hover:border-slate-400 hover:bg-slate-50 dark:border-white/20 dark:text-white dark:hover:border-white/40 dark:hover:bg-white/5'
          >
            {t('See what it can do')}
          </a>
        </motion.div>

        <motion.div {...fadeUp(0.35)} className='mt-8 flex justify-center'>
          <HappyUsers className='items-center' />
        </motion.div>

        {/* Product film */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className='relative mx-auto mt-16 max-w-5xl'
        >
          <div
            aria-hidden='true'
            className='pointer-events-none absolute -inset-8 rounded-[3rem] bg-gradient-to-r from-indigo-500/15 via-violet-500/10 to-fuchsia-500/15 blur-2xl'
          />
          <div className='relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-900 shadow-2xl ring-1 ring-slate-900/5 dark:border-white/10 dark:ring-white/10'>
            {}
            <video
              className='aspect-video w-full object-cover'
              src='https://media.easy-peasy.ai/assets/website-1024-hq.mp4'
              poster='/images/chat.png'
              autoPlay
              muted
              loop
              playsInline
              preload='metadata'
            />
          </div>
        </motion.div>

        {/* Frontier-model strip */}
        <motion.div {...fadeUp(0.5)} className='relative mt-12 overflow-hidden'>
          <div className='flex w-max items-center gap-x-8 animate-scroll-logos motion-reduce:animate-none'>
            {[false, true].map((isDuplicate) => (
              <React.Fragment key={isDuplicate ? 'dup' : 'main'}>
                {PROVIDERS.map((provider) => (
                  <div
                    key={`${isDuplicate ? 'dup-' : ''}${provider.name}`}
                    className='flex shrink-0 items-center gap-2 pr-8'
                    aria-hidden={isDuplicate || undefined}
                  >
                    <provider.Icon
                      aria-hidden='true'
                      className='h-[18px] w-auto shrink-0 fill-slate-400 [&_path]:fill-slate-400 dark:fill-white/50 dark:[&_path]:fill-white/50'
                    />
                    <span className='shrink-0 text-base font-normal text-slate-400 dark:text-white/50'>
                      {provider.name}
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
          <div className='pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent dark:from-slate-950' />
          <div className='pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent dark:from-slate-950' />
        </motion.div>
      </div>
    </section>
  );
};

export default MarkyHero;
